'use client';
import { createStyles, xref, memoizedEmitter, rem, getSize } from '@s7/styles';
import { isObject } from '@s7/utils';
import { IDENT } from './IconButton.contract.js';
import { defaultStylesParams } from './defaults.js';

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
var iconSizes = {
    xxs1: 24,
    xs1: 32,
    xs2: 36,
    sm1: 40,
    md1: 48,
    lg1: 56
};
var xrefIcon = xref(IDENT, "icon");
var xclassIcon = "." + xrefIcon;
var xrefLabel = xref(IDENT, "label");
var xclassLabel = "." + xrefLabel;
var isShadesIndependentVariant = function(variant) {
    return variant === "white" || variant === "secondary" || variant === "ghost";
};
var containerVariantCommonStyle = function(theme, variant) {
    return _extends({}, theme.fn.deriveStates(variant === "secondary" ? theme.palette.button.secondary : theme.palette.button.white, "light"), {
        focus: theme.palette.border.element
    });
};
var containerVariantStyles = {
    default: function(theme, shades) {
        var base = shades.base || shades[600];
        return _extends({
            default: base
        }, theme.fn.deriveStates(base, "color"), {
            focus: shades.mid || shades[200]
        });
    },
    light: function(theme, shades) {
        var base = shades.base || shades[600];
        return _extends({
            default: shades.min || shades[50]
        }, theme.fn.deriveStates(base, "color"), {
            hover: base,
            focus: shades.mid || shades[200]
        });
    },
    white: function(theme) {
        return _extends({
            default: theme.palette.button.white
        }, containerVariantCommonStyle(theme, "white"));
    },
    ghost: function(theme) {
        return _extends({
            default: "transparent"
        }, containerVariantCommonStyle(theme, "ghost"));
    },
    secondary: function(theme) {
        return _extends({
            default: theme.palette.button.secondary
        }, containerVariantCommonStyle(theme, "secondary"));
    }
};
var iconBackgrounds = {
    default: function(_, shades) {
        return shades.base || shades[600];
    },
    light: function(_, shades) {
        return shades.min || shades[50];
    },
    white: function(theme) {
        return theme.palette.button.white;
    },
    ghost: function() {
        return "transparent";
    },
    secondary: function(theme) {
        return theme.palette.button.secondary;
    }
};
var getIconBackground = memoizedEmitter(function(theme, shades, variant) {
    if (variant === void 0) variant = "default";
    return (iconBackgrounds[variant] || iconBackgrounds.default)(theme, shades);
});
var getContainerVariantStyles = memoizedEmitter(function(theme, shades, variant) {
    if (variant === void 0) variant = "default";
    return (containerVariantStyles[variant] || containerVariantStyles.default)(theme, shades);
});
var getLabelStyles = memoizedEmitter(function(theme, shades, variant) {
    if (variant === void 0) variant = "default";
    if (isShadesIndependentVariant(variant)) {
        return theme.palette.text.medium;
    }
    return shades.base || shades[600];
});
var getIconColor = memoizedEmitter(function(theme, shades, variant, state) {
    if (variant === void 0) variant = "default";
    if (state === void 0) state = "default";
    if (isShadesIndependentVariant(variant)) {
        return theme.palette.text.medium;
    }
    var base = shades.base || shades[600];
    return variant === "default" ? theme.palette.text.inverted : state === "default" ? base : theme.palette.text.onColor;
});
var getDisabledStyles = memoizedEmitter(function(theme, variant) {
    if (variant === void 0) variant = "default";
    var _obj;
    return _obj = {
        pointerEvents: "none"
    }, _obj[xclassIcon] = {
        background: variant === "ghost" ? "none" : theme.palette.background.highlight,
        color: variant === "ghost" ? theme.palette.text.quaternary : theme.palette.text.tertiary
    }, _obj[xclassLabel] = {
        color: variant === "ghost" ? theme.palette.text.quaternary : theme.palette.text.tertiary
    }, _obj;
});
var rootStyles = memoizedEmitter(function(theme, params) {
    var _$_extends = _extends({}, defaultStylesParams, params), color = _$_extends.color, _typography = _$_extends.typography, variant = _$_extends.variant;
    var _ref = _typography || {}; _ref.color; var typography = _object_without_properties_loose(_ref, [
        "color"
    ]);
    var shades = theme.fn.deriveShades(theme.fn.themeColor(color));
    var container = getContainerVariantStyles(theme, shades, variant);
    var iconColorDefault = getIconColor(theme, shades, variant, "hover");
    var iconOverlays = theme.fn.deriveStates(iconColorDefault, variant === "white" ? "dark" : "color");
    var labelColor = color ? theme.fn.primaryShade(theme.fn.themeColor(color)) : getLabelStyles(theme, shades, variant);
    var labelOverlays = theme.fn.deriveStates(labelColor, variant === "white" && !color ? "dark" : "color");
    var _obj, _obj1;
    return _extends({}, theme.fn.typography(_extends({
        size: "sm1",
        weight: "semibold"
    }, typography)), theme.fn.focusRing({
        color: container.focus,
        selector: "&:focus-visible " + xclassIcon
    }), {
        "&:hover": (_obj = {}, _obj[xclassIcon] = {
            background: variant === "white" ? container.default : container.hover,
            color: iconOverlays.hover
        }, _obj[xclassLabel] = {
            color: labelOverlays.hover
        }, _obj),
        "&:active": (_obj1 = {}, _obj1[xclassIcon] = {
            background: variant === "white" ? container.default : container.active,
            color: iconOverlays.active
        }, _obj1[xclassLabel] = {
            color: labelOverlays.active
        }, _obj1),
        "&:disabled,&[data-disabled=true]": getDisabledStyles(theme, variant)
    });
});
var sharedTransitionProps = {
    props: [
        "color",
        "background-color",
        "border-radius"
    ],
    fn: "ease-in-out"
};
var getShades = function(theme, color) {
    return theme.fn.deriveShades(theme.fn.themeColor(color));
};
var labelStyles = memoizedEmitter(function(theme, params) {
    var _$_extends = _extends({}, defaultStylesParams, params), transitionDuration = _$_extends.transitionDuration, color = _$_extends.color, variant = _$_extends.variant, typography = _$_extends.typography;
    var typographyColor = typography == null ? void 0 : typography.color;
    var textColor = isObject(typographyColor) || !typographyColor ? params.color : typographyColor;
    return _extends({
        color: textColor ? theme.fn.primaryShade(theme.fn.themeColor(textColor)) : getLabelStyles(theme, getShades(theme, color), variant)
    }, theme.fn.transition({
        props: "color",
        fn: "ease-in-out",
        duration: transitionDuration
    }));
});
var iconStyles = memoizedEmitter(function(theme, params) {
    var _$_extends = _extends({}, defaultStylesParams, params), transitionDuration = _$_extends.transitionDuration, variant = _$_extends.variant, size = _$_extends.size, color = _$_extends.color, radius = _$_extends.radius;
    var shades = getShades(theme, color);
    return _extends({
        background: getIconBackground(theme, shades, variant),
        color: getIconColor(theme, shades, variant)
    }, theme.fn.transition(_extends({}, sharedTransitionProps, {
        duration: transitionDuration
    })), theme.fn.square({
        size: rem(getSize({
            size: size,
            sizes: iconSizes
        })),
        radius: theme.fn.borderRadius(radius)
    }));
});
var useStyles = createStyles(function(theme, _param, _) {
    var breakpoints = _param.breakpoints, params = _object_without_properties_loose(_param, [
        "breakpoints"
    ]);
    return {
        root: _extends({
            position: "relative",
            userSelect: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "fit-content"
        }, rootStyles(theme, params), theme.fn.responsive(breakpoints)(function(params) {
            return rootStyles(theme, params);
        })),
        label: _extends({
            ref: xrefLabel,
            lineHeight: "100%"
        }, labelStyles(theme, params), theme.fn.responsive(breakpoints)(function(params) {
            return labelStyles(theme, params);
        })),
        icon: _extends({
            ref: xrefIcon,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
        }, iconStyles(theme, params), theme.fn.responsive(breakpoints)(function(params) {
            return iconStyles(theme, params);
        })),
        leftIcon: {},
        rightIcon: {}
    };
});

export { useStyles as default };
